<template>
	<div class="stretch-layout">
		<title-bar with-search :title="$t('param_label.participants', {count: viewData.participants_count}, viewData.participants_count)"
				   flat v-model="searchQuery"/>

		<card xs-tile sm-tile class="flex-grow-1">
			<profile-preview :model="viewData.event" :to="$web('events.show', viewData.event._key)" class="border-b" flat :style="{borderTop: `2px solid ${viewData.event.color ?? clubStore.colorStyle}`}"/>

			<lazy-missing-info v-if="!viewData.participationClasses.length" :icon="icons.event" no-spacing
							   :button-icon="icons.search" class="flex-grow-1"
							   :button="$t('action.participant.index')"
							   :to="$web('events.participations.index', viewData.event._key)"
							   :text="$t('missing.participations.index')"/>
			<lazy-missing-info v-else-if="!displayParticipationClasses.length" :icon="icons.event"
							   :button-icon="icons.search" class="flex-grow-1"
							   :button="$t('action.participant.index')"
							   :to="$web('events.participations.index', viewData.event._key)"
							   :text="$t('missing.search')"/>
			<expansion-panels v-else open>
				<expansion-panel v-for="displayParticipationClass in displayParticipationClasses" :key="displayParticipationClass.id"
								 padding="px-16 lg:px-24"
								 :title="displayParticipationClass.name" :subtitle="$t('param_label.participants', {count: displayParticipationClass.participations.length}, displayParticipationClass.participations.length)">
					<participation-preview v-for="participation in displayParticipationClass.participations" :key="`participation-${participation.id}`" :participation="participation"
										   :event="viewData.event" @show-likes="showLikes(viewData.event, participation)"
										   @show-comments="showComments(viewData.event, participation)"/>
				</expansion-panel>
			</expansion-panels>
		</card>

		<lazy-participation-interactivity-modals v-if="selectedEvent && selectedParticipation" :event="selectedEvent" :participation="selectedParticipation"
												 v-model:likes="showLikesModal" v-model:comments="showCommentsModal"/>
	</div>
</template>

<script setup lang="ts">
import {mdiCalendarMultiple} from '@mdi/js';
import {searchIcon} from "@spoferan/nuxt-spoferan/icons";
import {useClubStore} from "../../../../../store/club";
import {useIndexStore} from "../../../../../store";
import {debounce} from "@spoferan/spoferan-ts-core";

const route = useRoute();
const {$api} = useNuxtApp();
const {t} = useI18n();
const indexStore = useIndexStore();
const clubStore = useClubStore();

const {data: viewData} = await useApiFetch(`/v1/views/clubs/${clubStore.club.id}/events/${route.params.event}/participations`, {
	guest: true
});

if (viewData.event && viewData.event._key !== route.params.event) {
	await navigateTo({params: {event: viewData.event._key}}, {
		redirectCode: 308
	});
}

const icons = {
	event: mdiCalendarMultiple,
	search: searchIcon,
}

const {updateUrlQuery} = useUrlMutations();
const filterViewBySearchQuery = () => {
	const query = searchQuery.value && searchQuery.value.length ? searchQuery.value.toLowerCase() : null;
	if (!query) {
		displayParticipationClasses.value = (viewData.participationClasses ?? []);
	} else {
		const filteredParticipationClasses = [];
		(viewData.participationClasses ?? []).forEach(participationClass => {
			const filteredParticipations = participationClass.participations.filter(participation => {
				return participation.display_name.toLowerCase().includes(query) ||
					participation.starter_number?.toLowerCase().includes(query) ||
					participation.team?.display_name.toLowerCase().includes(query);
			});
			if (filteredParticipations.length) {
				filteredParticipationClasses.push({
					...participationClass,
					participations: filteredParticipations
				});
			}
		});

		displayParticipationClasses.value = filteredParticipationClasses;

	}
};

const searchQuery = ref(route.query.search);
const displayParticipationClasses = ref([]);
filterViewBySearchQuery();
watch(searchQuery, () => {
	filterViewBySearchQueryInput()
});
const filterViewBySearchQueryInput = debounce(function () {
	filterViewBySearchQuery()
	updateUrlQuery({search: searchQuery.value});
}, 300);

const showLikesModal = ref(false);
const showCommentsModal = ref(false);
const selectedEvent = ref(null);
const selectedParticipation = ref(null);

function showLikes(event, participation) {
	selectedEvent.value = event;
	selectedParticipation.value = participation;

	nextTick(() => {
		showLikesModal.value = true;
	});
}

function showComments(event, participation) {
	selectedEvent.value = event;
	selectedParticipation.value = participation;

	nextTick(() => {
		showCommentsModal.value = true;
	});
}

useMeta({
	title: clubStore.buildPageTitle(t('view.clubs.events.participations.index.title', {club: clubStore.club.display_name, event: viewData.event.display_name})),
	description: t('view.clubs.events.participations.index.description', {club: clubStore.club.display_name, event: viewData.event.display_name}),
	noIndex: true
})

</script>

